import {
  Component,
  OnInit,
  OnDestroy,
  Input,
  Output,
  OnChanges,
  EventEmitter,
  SimpleChanges,
} from '@angular/core';
import { UntypedFormGroup, UntypedFormBuilder, Validators, AbstractControl } from '@angular/forms';

import { Subscription, tap, catchError, of, BehaviorSubject } from 'rxjs';
import { NgbCalendar, NgbDate, NgbDateAdapter } from '@ng-bootstrap/ng-bootstrap';

import { ExportTypes } from '@shared/export/enums';
import { Location } from '@shared/location/models';
import {
  DateService,
  CustomAdapter,
  CustomDateParserFormatterProvider,
} from '@tablesready/common-ui';
import { ExportDataService } from '../../services/export-data.service';
import { AppIcon, AppIconSize } from '@tablesready/frontend-icons';
import { AlertsService, IAlertTypes } from '@tablesready/frontend-alerts-bar';

@Component({
  selector: 'tablesready-export-data',
  templateUrl: './export-data.component.html',
  providers: [
    {
      provide: NgbDateAdapter,
      useClass: CustomAdapter,
    },
    CustomDateParserFormatterProvider,
  ],
})
export class ExportDataComponent implements OnInit, OnChanges, OnDestroy {
  @Input() user: Location;
  @Input() locations: Location[];
  @Input() isOnBusinessPlan = false;
  @Output() freePlanDetected = new EventEmitter<void>();

  icon = AppIcon;
  iconSize = AppIconSize;
  exportDataForm: UntypedFormGroup;
  exportDataTypes: {name: string, value: string}[];
  fromDate: string;
  toDate: string;
  dateOptions$ = new BehaviorSubject<{minDate: NgbDate; maxDate: NgbDate}>(null);

  private readonly subscriptions = new Subscription();

  constructor(
    private readonly fb: UntypedFormBuilder,
    private readonly calendar: NgbCalendar,
    private readonly dateAdapter: NgbDateAdapter<string>,
    private readonly alertsService: AlertsService,
    private readonly dateService: DateService,
    private readonly exportDataService: ExportDataService,
  ) {}

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.locations && typeof this.locations === 'object') {
      this.locations.unshift({
        _id: 'all',
        name: 'All locations',
      } as Location);
    }
  }

  ngOnInit(): void {
    const labels = this.user.displayOptions.labels;
    const minDate = this.dateService.formatTimestampToDate(new Date(this.user.created_date));

    this.exportDataTypes = [
      {
        name: labels.customer_plural === 'parties' ? labels.customer_plural : 'visits',
        value: ExportTypes.parties,
      },
      {
        name: 'Customers',
        value: ExportTypes.customers,
      },
      {
        name: 'Notifications',
        value: ExportTypes.partyNotifications,
      },
    ];

    this.fromDate = this.dateAdapter.toModel(this.user.created_date
      ? this.dateService.formatTimestampToDate(new Date(this.user.created_date))
      : this.dateService.formatTimestampToDate(new Date(new Date().getFullYear(), 0, 1)));
    this.toDate = this.dateAdapter.toModel(this.dateService.formatTimestampToDate(new Date()));

    this.exportDataForm = this.fb.group({
      location: ['all'],
      exportDataType: [this.exportDataTypes[0].value, Validators.required],
      fromDate: [this.fromDate, Validators.required],
      toDate: [this.toDate, Validators.required],
    });

    this.dateOptions$.next({
      minDate: new NgbDate(minDate.year, minDate.month, minDate.day),
      maxDate: this.calendar.getToday(),
    });
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }

  get ngbStartDateValue(): NgbDate {
    if (this.startDateControl.value) {
      return this.dateService.formatStringToNgbDate(this.startDateControl.value);
    }

    return null;
  }

  get ngbEndDateValue(): NgbDate {
    if (this.endDateControl.value) {
      return this.dateService.formatStringToNgbDate(this.endDateControl.value);
    }

    return null;
  }

  get exportDataTypeControl(): AbstractControl {
    return this.exportDataForm.get('exportDataType');
  }

  get startDateControl(): AbstractControl {
    return this.exportDataForm.get('fromDate');
  }

  get endDateControl(): AbstractControl {
    return this.exportDataForm.get('toDate');
  }

  submit(): void {
    if (!this.isOnBusinessPlan) {
      this.freePlanDetected.emit();

      return;
    }

    const value = this.exportDataForm.value;
    const options = {
      locationId: value.location === 'all' ? null : value.location,
      startDate: this.dateService.formatNgbDateToTimestamp(value.fromDate, 0, 0),
      endDate: this.dateService.formatNgbDateToTimestamp(value.toDate, 23, 59),
      dataType: value.exportDataType,
    };

    this.subscriptions.add(
      this.exportDataService.setExport(options)
        .pipe(
          tap(() => {
            this.alertsService.add({
              type: IAlertTypes.success,
              heading: 'Success:',
              body: 'Parties will be exported. Check back here for the file download.',
            });
          }),
          catchError(error => {
            this.alertsService.add({
              type: IAlertTypes.danger,
              heading: 'Error:',
              body: `unable to export parties data: ${error}.`,
            });
            return of(null);
          })
        ).subscribe(),
    );
  }
}
